import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Autocomplete,
  Typography,
  Switch,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { CATEGORIES, MESSAGE_TYPES, SEVERITY_LEVELS } from "./utils";

export default function SendMessageDialog({
  open,
  onClose,
  sendForm,
  setSendForm,
  templates,
  selectedUsers,
  setSelectedUsers,
  userSearchInput,
  setUserSearchInput,
  userSearchResults,
  onTemplateSelect,
  onSend,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send System Message</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Box>
              <Typography variant="body1" fontWeight="medium" p={0} m={0}>
                Broadcast to All Users
              </Typography>
              <Typography variant="caption" color="text.secondary" p={0} m={0}>
                Send this message to all registered users
              </Typography>
            </Box>
            <Switch
              checked={sendForm.isBroadcast}
              onChange={(e) => {
                setSendForm({ ...sendForm, isBroadcast: e.target.checked });
                if (e.target.checked) {
                  setSelectedUsers([]);
                  setSendForm((prev) => ({ ...prev, recipientIds: [] }));
                  setUserSearchInput("");
                }
              }}
              color="primary"
            />
          </Box>

          {!sendForm.isBroadcast && (
            <Autocomplete
              multiple
              freeSolo={false}
              options={userSearchResults}
              getOptionLabel={(option) =>
                `${option.fullname || ""} (@${option.username || option._id}) - ${
                  option.email || ""
                }`
              }
              value={selectedUsers}
              onChange={(e, newValue) => {
                setSelectedUsers(newValue);
                setSendForm({
                  ...sendForm,
                  recipientIds: newValue.map((u) => u._id),
                });
              }}
              inputValue={userSearchInput}
              onInputChange={(e, value, reason) => {
                if (reason === "input") {
                  setUserSearchInput(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Recipients"
                  placeholder="Type to search by name, username or email..."
                  required
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Use Template (Optional)</InputLabel>
            <Select
              value={sendForm.templateId}
              label="Use Template (Optional)"
              onChange={(e) => onTemplateSelect(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {templates
                .filter((t) => t.isActive)
                .map((template) => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {!sendForm.templateId && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={sendForm.category}
                label="Category"
                onChange={(e) =>
                  setSendForm({ ...sendForm, category: e.target.value })
                }
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={sendForm.type}
                  label="Type"
                  onChange={(e) =>
                    setSendForm({ ...sendForm, type: e.target.value })
                  }
                >
                  {MESSAGE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={sendForm.severity}
                  label="Severity"
                  onChange={(e) =>
                    setSendForm({ ...sendForm, severity: e.target.value })
                  }
                >
                  {SEVERITY_LEVELS.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label="Message Title"
            value={sendForm.customTitle}
            onChange={(e) =>
              setSendForm({ ...sendForm, customTitle: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Message Content"
            multiline
            rows={4}
            value={sendForm.customMessage}
            onChange={(e) =>
              setSendForm({ ...sendForm, customMessage: e.target.value })
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSend}
          variant="contained"
          startIcon={<SendIcon />}
          disabled={
            !sendForm.customTitle ||
            !sendForm.customMessage ||
            (!sendForm.isBroadcast && sendForm.recipientIds.length === 0)
          }
        >
          {sendForm.isBroadcast ? "Broadcast Message" : "Send Message"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
